import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { ServiceCategoryEnum } from "../../types/service.type";

import { GO_SERVICE_QUERY_KEYS } from "../queryKeys";
import { getOrderedContracts } from "../services/goHireService";

const CONTRACTS_PAGE_LIMIT = 3;

interface UseOrderedContractsParams {
  limit?: number;
  offset?: number;
  category?: ServiceCategoryEnum;
  sort?: Record<string, "ASC" | "DESC">;
  enabled?: boolean;
  userId: string;
}

interface UseOrderedContractsPaginatedParams extends Omit<UseOrderedContractsParams, "offset"> {
  page?: number;
}

export const useOrderedContractsPaginated = ({
  limit = CONTRACTS_PAGE_LIMIT,
  page = 0,
  category,
  sort,
  enabled,
  userId,
}: UseOrderedContractsPaginatedParams) => {
  return useQuery({
    queryKey: [...GO_SERVICE_QUERY_KEYS.orderedContracts(), { limit, page, category, sort, userId }],
    queryFn: () => getOrderedContracts({ limit, offset: page * limit, category, sort, userId }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!category && enabled && !!userId,
  });
};

export const useOrderedContractsInfinite = ({
  limit = CONTRACTS_PAGE_LIMIT,
  category,
  sort,
  enabled,
  userId,
}: Omit<UseOrderedContractsParams, "offset">) => {
  return useInfiniteQuery({
    queryKey: [...GO_SERVICE_QUERY_KEYS.orderedContracts(), { limit, category, sort, userId }],
    queryFn: ({ pageParam = 0 }) => getOrderedContracts({ limit, offset: pageParam * limit, category, sort, userId }),
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.length * limit;
      return fetchedCount < lastPage.data.meta.totalItems ? allPages.length : undefined;
    },
    initialPageParam: 0,
    enabled: !!category && enabled && !!userId,
  });
};
